import React from 'react';
import { Link as RouterLink, NavLink, NavLinkProps, LinkProps as RouterLinkProps } from 'react-router-dom';
import { createTheme } from '@mui/material/styles';
import { error, info, neutral, primary, success, warning } from '_lex/_foundations/phxColors';

const LinkBehavior = React.forwardRef<HTMLAnchorElement, Omit<RouterLinkProps, 'to'> & { href: RouterLinkProps['to'] }>(
  (props, ref) => {
    const { href, ...other } = props;
    // Map href (Material UI) -> to (react-router)
    return (
      <RouterLink
        ref={ref}
        to={href}
        {...other}
      />
    );
  }
);

const NavLinkBehavior = React.forwardRef<HTMLAnchorElement, Omit<NavLinkProps, 'to'> & { href: NavLinkProps['to'] }>(
  (props, ref) => {
    const { href, ...other } = props;
    // Map href (Material UI) -> to (react-router)
    return (
      <NavLink
        ref={ref}
        to={href}
        {...other}
      />
    );
  }
);

declare module '@mui/material/styles/createPalette' {
  interface Palette {
    neutral: any;
  }

  interface PaletteOptions {
    neutral?: any;
  }
}

declare module '@mui/material' {
  interface ButtonPropsColorOverrides {
    neutral: true;
    dark: true;
  }

  interface SvgIconPropsColorOverrides {
    neutral: true;
  }

  interface BadgePropsVariantOverrides {
    inline: true;
  }

  interface BadgePropsColorOverrides {
    neutral: true;
  }

  interface ChipPropsColorOverrides {
    neutral: true;
  }

  interface IconButtonPropsColorOverrides {
    neutral: true;
  }

  interface TypographyVariants {
    listLink: React.CSSProperties;
    listLinkBold: React.CSSProperties;
    // Display variants
    displayXl: React.CSSProperties;
    displayLg: React.CSSProperties;
    displayMd: React.CSSProperties;
    displaySm: React.CSSProperties;
    // Subtitle variants
    subtitleLg: React.CSSProperties;
    subtitleMd: React.CSSProperties;
    subtitleSm: React.CSSProperties;
    // Button variants
    buttonXl: React.CSSProperties;
    buttonLg: React.CSSProperties;
    buttonMd: React.CSSProperties;
    buttonSm: React.CSSProperties;
    // Body variants
    bodyLg: React.CSSProperties;
    bodyMd: React.CSSProperties;
    bodySm: React.CSSProperties;
    bodyXs: React.CSSProperties;
    // Link variants
    linkLg: React.CSSProperties;
    linkMd: React.CSSProperties;
    linkSm: React.CSSProperties;
    linkXs: React.CSSProperties;
    // Label variants
    labelMd: React.CSSProperties;
    labelSm: React.CSSProperties;
    // Eyebrow variants
    eyebrowLg: React.CSSProperties;
    eyebrowMd: React.CSSProperties;
    eyebrowSm: React.CSSProperties;
    // Caption variants
    captionMd: React.CSSProperties;
    captionSm: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    listLink?: React.CSSProperties;
    listLinkBold?: React.CSSProperties;
    // Display variants
    displayXl?: React.CSSProperties;
    displayLg?: React.CSSProperties;
    displayMd?: React.CSSProperties;
    displaySm?: React.CSSProperties;
    // Subtitle variants
    subtitleLg?: React.CSSProperties;
    subtitleMd?: React.CSSProperties;
    subtitleSm?: React.CSSProperties;
    // Button variants
    buttonXl?: React.CSSProperties;
    buttonLg?: React.CSSProperties;
    buttonMd?: React.CSSProperties;
    buttonSm?: React.CSSProperties;
    // Body variants
    bodyLg?: React.CSSProperties;
    bodyMd?: React.CSSProperties;
    bodySm?: React.CSSProperties;
    bodyXs?: React.CSSProperties;
    // Link variants
    linkLg?: React.CSSProperties;
    linkMd?: React.CSSProperties;
    linkSm?: React.CSSProperties;
    linkXs?: React.CSSProperties;
    // Label variants
    labelMd?: React.CSSProperties;
    labelSm?: React.CSSProperties;
    // Eyebrow variants
    eyebrowLg?: React.CSSProperties;
    eyebrowMd?: React.CSSProperties;
    eyebrowSm?: React.CSSProperties;
    // Caption variants
    captionMd?: React.CSSProperties;
    captionSm?: React.CSSProperties;
  }
  interface TypographyPropsVariantOverrides {
    listLink: true;
    listLinkBold: true;
    // Display variants
    displayXl: true;
    displayLg: true;
    displayMd: true;
    displaySm: true;
    // Subtitle variants
    subtitleLg: true;
    subtitleMd: true;
    subtitleSm: true;
    // Button variants
    buttonXl: true;
    buttonLg: true;
    buttonMd: true;
    buttonSm: true;
    // Body variants
    bodyLg: true;
    bodyMd: true;
    bodySm: true;
    bodyXs: true;
    // Link variants
    linkLg: true;
    linkMd: true;
    linkSm: true;
    linkXs: true;
    // Label variants
    labelMd: true;
    labelSm: true;
    // Eyebrow variants
    eyebrowLg: true;
    eyebrowMd: true;
    eyebrowSm: true;
    // Caption variants
    captionMd: true;
    captionSm: true;
  }
  interface ButtonPropsVariantOverrides {
    neutral: true;
    dark: true;
  }

  interface SwitchPropsSizeOverrides {
    xs: true;
  }
}

const defaultTheme = createTheme();
const shadowsArr = defaultTheme.shadows;
shadowsArr[1] = '0px 1px 2px 0px rgba(0, 0, 0, 0.08)';
const phoenixTheme = createTheme({
  typography: {
    fontFamily: 'Inter',
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
    h1: {
      fontSize: '1.8rem',
      fontWeight: 600,
    },
    h2: {
      fontSize: '1.6rem',
      fontWeight: 600,
    },
    h3: {
      fontSize: '1.4rem',
      fontWeight: 600,
    },
    h4: {
      fontSize: '1.2rem',
      fontWeight: 600,
    },
    h5: {
      fontSize: '1rem',
      fontWeight: 600,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
    },
    body1: {
      fontSize: '0.875rem',
    },
    listLinkBold: {
      fontWeight: 600,
      cursor: 'pointer',
      color: primary[900],
    },
    listLink: {
      cursor: 'pointer',
      color: primary[900],
    },
    // Display variants
    displayXl: {
      fontSize: '56px',
      lineHeight: 1.2,
      fontWeight: 600,
      letterSpacing: '-1.68px',
    },
    displayLg: {
      fontSize: '44px',
      lineHeight: 1.2,
      fontWeight: 500,
      letterSpacing: '-1.44px',
    },
    displayMd: {
      fontSize: '32px',
      lineHeight: 1.2,
      fontWeight: 500,
      letterSpacing: '-1.2px',
    },
    displaySm: {
      fontSize: '24px',
      lineHeight: 1.2,
      fontWeight: 500,
      letterSpacing: '-0.96px',
    },
    // Subtitle variants
    subtitleLg: {
      fontSize: '18px',
      lineHeight: 1.6,
      fontWeight: 500,
      letterSpacing: '-0.72px',
    },
    subtitleMd: {
      fontSize: '16px',
      lineHeight: 1.6,
      fontWeight: 500,
      letterSpacing: '-0.6px',
    },
    subtitleSm: {
      fontSize: '14px',
      lineHeight: 1.5,
      fontWeight: 500,
      letterSpacing: '-0.48px',
    },
    // Button variants
    buttonXl: {
      fontSize: '22px',
      lineHeight: 1.2,
      fontWeight: 500,
      letterSpacing: '-0.54px',
    },
    buttonLg: {
      fontSize: '18px',
      lineHeight: 1.2,
      fontWeight: 500,
      letterSpacing: '-0.48px',
    },
    buttonMd: {
      fontSize: '16px',
      lineHeight: 1.2,
      fontWeight: 500,
      letterSpacing: '-0.42px',
    },
    buttonSm: {
      fontSize: '14px',
      lineHeight: 1.2,
      fontWeight: 500,
      letterSpacing: '-0.36px',
    },
    // Body variants
    bodyLg: {
      fontSize: '18px',
      lineHeight: 1.6,
      fontWeight: 400,
      letterSpacing: '-0.54px',
    },
    bodyMd: {
      fontSize: '16px',
      lineHeight: 1.5,
      fontWeight: 400,
      letterSpacing: '-0.48px',
    },
    bodySm: {
      fontSize: '14px',
      lineHeight: 1.5,
      fontWeight: 400,
      letterSpacing: '-0.42px',
    },
    bodyXs: {
      fontSize: '12px',
      lineHeight: 1.4,
      fontWeight: 400,
      letterSpacing: '-0.36px',
    },
    // Link variants
    linkLg: {
      fontSize: '18px',
      lineHeight: 1.6,
      fontWeight: 400,
      letterSpacing: '-0.54px',
      textDecoration: 'underline',
    },
    linkMd: {
      fontSize: '16px',
      lineHeight: 1.5,
      fontWeight: 400,
      letterSpacing: '-0.48px',
      textDecoration: 'underline',
    },
    linkSm: {
      fontSize: '14px',
      lineHeight: 1.5,
      fontWeight: 400,
      letterSpacing: '-0.42px',
      textDecoration: 'underline',
    },
    linkXs: {
      fontSize: '12px',
      lineHeight: 1.4,
      fontWeight: 400,
      letterSpacing: '-0.36px',
      textDecoration: 'underline',
    },
    // Label variants
    labelMd: {
      fontSize: '16px',
      lineHeight: 1.2,
      fontWeight: 500,
      letterSpacing: '-0.48px',
    },
    labelSm: {
      fontSize: '14px',
      lineHeight: 1.2,
      fontWeight: 500,
      letterSpacing: '-0.42px',
    },
    // Eyebrow variants
    eyebrowLg: {
      fontSize: '16px',
      lineHeight: 1.6,
      fontWeight: 500,
      letterSpacing: '0.1px',
      textTransform: 'uppercase',
    },
    eyebrowMd: {
      fontSize: '14px',
      lineHeight: 1.5,
      fontWeight: 500,
      letterSpacing: '1px',
      textTransform: 'uppercase',
    },
    eyebrowSm: {
      fontSize: '12px',
      lineHeight: 1.4,
      fontWeight: 400,
      letterSpacing: '1px',
      textTransform: 'uppercase',
    },
    // Caption variants
    captionMd: {
      fontSize: '12px',
      lineHeight: 1.2,
      fontWeight: 400,
      letterSpacing: '-0.36px',
    },
    captionSm: {
      fontSize: '10px',
      lineHeight: 1.2,
      fontWeight: 400,
      letterSpacing: '-0.24px',
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: shadowsArr[1],
          '&.takeover-nav': {
            '.MuiIconButton-root': {
              border: 'solid 1px rgba(255, 255, 255, 0.6)',
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.1)',
              padding: 4,
              '.MuiSvgIcon-root': {
                fill: neutral[25],
              },
            },
            '.MuiTypography-h3': {
              fontSize: '1.1rem',
              lineHeight: '34px',
            },
          },
          '&.lex-navigation-header': {
            height: 68,
            minHeight: 68,
            padding: 0,
            backgroundColor: primary[900],
            color: neutral[25],
            '&.lex-navigation-header--global': {
              backgroundColor: primary[800],
            },
            '& .MuiToolbar-root': {
              padding: '16px 20px',
            },
            '& .MuiButtonBase-root.MuiButton-sizeSmall.icon-only': {
              color: neutral[25],
              fill: neutral[25],
              border: 'solid 1px rgba(255, 255, 255, 0.5)',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              },
            },
          },
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        root: {
          '& .MuiBadge-inline': {
            transform: 'none',
            position: 'static',
            fontWeight: 600,
            fontSize: '0.8rem',
            height: '1.5rem',
            lineHeight: '1.25rem',
            padding: '4px 12px',
            borderRadius: '9999px',
            '&.MuiBadge-colorNeutral': {
              backgroundColor: neutral[100],
            },
            '&.MuiBadge-colorSuccess': {
              backgroundColor: success[100],
              color: success[900],
            },
            '&.MuiBadge-colorError': {
              backgroundColor: error[100],
              color: error[900],
            },
          },
          '& .MuiBadge-colorNeutral': {
            backgroundColor: neutral[300],
          },
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          'li, li>div': {
            maxWidth: 180,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'contained',
        size: 'medium',
        disableElevation: true,
        sx: {
          minWidth: 30,
        },
      },
      variants: [
        {
          props: { variant: 'dark' },
          style: {
            backgroundColor: 'transparent',
            color: neutral[600],
            border: 'solid 1px transparent',
            '&:hover': {
              backgroundColor: neutral[800],
              color: neutral[600],
            },
            '&:focus, &:active': {
              border: `solid 1px ${neutral[600]}`,
              color: neutral[600],
            },
            '&.Mui-disabled': {
              backgroundColor: 'transparent',
              color: neutral[400],
              border: 'solid 1px transparent',
              '&:hover': {
                borderColor: 'transparent',
              },
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          borderRadius: 4,
          '&.MuiButton-sizeSmall': {
            padding: '8px 16px',
            fontSize: 16,
            lineHeight: '18px',
            '&.icon-only': {
              minWidth: 0,
              padding: '0px !important',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: 36,
              height: 36,
            },
            '&.xs': {
              fontSize: 12,
            },
            '&.icon-only.xs': {
              width: 30,
              height: 30,
            },
          },
          '&.MuiButton-sizeMedium': {
            padding: '12px 24px',
            fontSize: 18,
            lineHeight: '20px',
            '&.icon-only': {
              minWidth: 0,
              padding: '0px !important',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: 46,
              height: 46,
            },
          },
          '&.MuiButton-sizeLarge': {
            borderRadius: 8,
            padding: '16px 32px',
            fontSize: 22,
            lineHeight: '24px',
            '&.icon-only': {
              minWidth: 0,
              padding: '0px !important',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: 58,
              height: 58,
            },
          },
          '&.MuiButton-containedNeutral': {
            backgroundColor: neutral[200],
          },
          '&.MuiButton-outlinedNeutral': {
            color: neutral[800],
            border: `solid 1px ${neutral[200]}`,
            '&:hover': {
              borderColor: neutral[300],
            },
            '&.Mui-disabled': {
              color: neutral[400],
              border: `solid 1px ${neutral[200]}`,
              '&:hover': {
                borderColor: neutral[200],
              },
            },
          },
          '&.MuiButton-containedPrimary': {
            backgroundColor: primary[800],
            color: neutral[25],
            border: `solid 1px ${primary[800]}`,
            '&:hover': {
              borderColor: primary[900],
            },
            '&.Mui-disabled': {
              backgroundColor: neutral[100],
              color: neutral[400],
              border: `solid 1px ${neutral[200]}`,
              '&:hover': {
                borderColor: neutral[200],
              },
            },
          },
          '&.MuiButton-containedSecondary': {
            backgroundColor: neutral[25],
            color: neutral[900],
            border: `solid 1px ${neutral[200]}`,
            '.MuiButton-icon': {
              color: neutral[500],
            },
            '&:hover': {
              borderColor: neutral[300],
            },
            '&.Mui-disabled': {
              backgroundColor: neutral[100],
              color: neutral[400],
              border: `solid 1px ${neutral[200]}`,
              '&:hover': {
                borderColor: neutral[200],
              },
            },
          },
          '&.MuiButton-containedError': {
            backgroundColor: error[600],
            color: neutral[25],
            border: `solid 1px ${error[600]}`,
            '&.Mui-disabled': {
              backgroundColor: neutral[100],
              color: neutral[400],
              border: `solid 1px ${neutral[200]}`,
              '&:hover': {
                borderColor: neutral[200],
              },
            },
          },
          '&.MuiButton-containedSuccess': {
            backgroundColor: success[600],
            color: neutral[25],
            border: `solid 1px ${success[600]}`,
            '&.Mui-disabled': {
              backgroundColor: neutral[100],
              color: neutral[400],
              border: `solid 1px ${neutral[200]}`,
              '&:hover': {
                borderColor: neutral[200],
              },
            },
          },
          '&.MuiButton-containedWarning': {
            backgroundColor: warning[600],
            color: neutral[25],
            border: `solid 1px ${warning[600]}`,
            '&.Mui-disabled': {
              backgroundColor: neutral[100],
              color: neutral[400],
              border: `solid 1px ${neutral[200]}`,
              '&:hover': {
                borderColor: neutral[200],
              },
            },
          },
          '&.MuiButton-textPrimary': {
            backgroundColor: 'transparent',
            color: primary[800],
            border: 'solid 1px transparent',
            '&:hover': {
              backgroundColor: 'transparent',
              color: primary[900],
            },
            '&.Mui-disabled': {
              backgroundColor: 'transparent',
              color: neutral[400],
              border: 'solid 1px transparent',
              '&:hover': {
                borderColor: 'transparent',
              },
            },
          },
          '&.MuiButton-darkNeutral': {
            backgroundColor: 'transparent',
            color: neutral[600],
            border: 'solid 1px transparent',
            '&:hover': {
              backgroundColor: neutral[25],
              color: neutral[800],
            },
            '&:focus, &:active': {
              border: `solid 1px ${neutral[600]}`,
              color: neutral[600],
            },
            '&.Mui-disabled': {
              backgroundColor: 'transparent',
              color: neutral[400],
              border: 'solid 1px transparent',
              '&:hover': {
                borderColor: 'transparent',
              },
            },
          },
          '&.MuiButton-root:not(.icon-only):focus': {
            boxShadow: '0px 0px 0px 2px #FFF, 0px 0px 0px 6px rgba(0,78,235,0.50)',
          },
          '&.MuiButton-containedPrimary .MuiBadge-badge': {
            backgroundColor: '#FFF',
            color: primary[800],
            padding: 4,
            minWidth: '1.5rem',
          },
          '&.MuiButton-textPrimary .MuiBadge-badge': {
            border: `solid 1px ${neutral[500]}`,
            color: neutral[800],
            padding: 4,
            minWidth: '1.5rem',
          },
          '&.icon-only': {
            minWidth: 0,
            '.MuiButton-icon': {
              margin: 0,
            },
          },
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        LinkComponent: LinkBehavior,
      },
    },
    MuiCard: {
      defaultProps: {
        variant: 'outlined',
        sx: {
          borderRadius: 3,
          borderColor: neutral[200],
        },
      },
      styleOverrides: {
        root: {
          '& .MuiCardContent-root': {
            maxHeight: '100%',
            overflow: 'auto',
          },
          '&.phx-card': {
            backgroundColor: neutral[25],
            borderRadius: 12,
            border: `solid 4px ${neutral[50]}`,
            boxShadow: `0 0 1px 1px ${neutral[100]} inset`,
            padding: 24,
          },
          '&.lex-chart-card': {
            backgroundColor: neutral[50],
            borderRadius: 12,
            border: `solid 1px ${neutral[100]}`,
            '& .MuiCardHeader-root': {
              padding: '16px 24px',
              '& .MuiCardHeader-title': {
                fontSize: '18px',
                lineHeight: 1.5,
                fontWeight: 500,
                letterSpacing: '0.1px',
              },
            },
            '& .MuiCardContent-root': {
              padding: '16px 24px',
            },
          },
          '&.lex-chart-card-white': {
            backgroundColor: neutral[25],
            borderRadius: 12,
            border: `solid 1px ${neutral[100]}`,
            '& .MuiCardHeader-root': {
              padding: '16px 24px',
              '& .MuiCardHeader-title': {
                fontSize: '18px',
                lineHeight: 1.5,
                fontWeight: 500,
                letterSpacing: '0.1px',
              },
            },
            '& .MuiCardContent-root': {
              padding: '16px 24px',
            },
          },
          '&.lex-metric-card': {
            backgroundColor: neutral[25],
            borderRadius: 8,
            border: `solid 1px ${neutral[300]}`,
            gap: 8,
            padding: 16,
          },
          '&.lex-card': {
            borderRadius: 4,
            border: `solid 1px ${neutral[200]}`,
            '& .MuiCardHeader-root': {
              borderBottom: `solid 1px ${neutral[200]}`,
              backgroundColor: neutral[50],
              padding: '16px 24px',
              '& .MuiCardHeader-title': {
                fontSize: '18px',
                lineHeight: 1.5,
                fontWeight: 500,
                letterSpacing: '0.1px',
              },
            },
          },
          '&.lex-dashboard-card': {
            backgroundColor: 'transparent',
            border: 'none',
            '& > .MuiCardHeader-root': {
              borderBottom: 0,
              padding: '0.75rem 0',
              '& .MuiCardHeader-action': {
                margin: 0,
                padding: 0,
                alignSelf: 'center',
              },
            },
            '& > .MuiCardContent-root': {
              padding: 0,
            },
          },
          '&.lex-drawer-card': {
            borderRadius: 0,
            border: 'none',
            borderBottom: `solid 1px ${neutral[200]}`,
            '& .MuiCardHeader-root': {
              borderBottom: `solid 1px ${neutral[200]}`,
              backgroundColor: neutral[50],
              padding: '12px 16px',
              '& .MuiCardHeader-title': {
                fontSize: '14px',
                lineHeight: 1.5,
                fontWeight: 500,
                letterSpacing: '0.1px',
                textTransform: 'uppercase',
                color: neutral[500],
              },
            },
            '& .MuiCardContent-root .MuiTypography-root': {
              color: `${neutral[800]} !important`,
            },
          },
          '&.lex-select-card': {
            borderRadius: 8,
            border: `solid 1px ${neutral[200]}`,
            cursor: 'pointer',
            '&:hover': {
              borderColor: primary[500],
            },
            '&.selected': {
              borderColor: primary[800],
              backgroundColor: primary[50],
              '& .lex-select-card__icon': {
                borderColor: primary[600],
              },
            },
            '&.disabled': {
              cursor: 'not-allowed',
              opacity: 0.6,
              backgroundColor: neutral[50],
              '&:hover': {
                borderColor: neutral[200],
              },
            },
            '&.add-item': {
              borderColor: 'transparent',
              '& .lex-select-card__icon': {
                border: `1px dashed ${primary[800]}`,
                backgroundColor: primary[50],
              },
            },
            '& .MuiCardContent-root': {
              padding: 16,
              '&:last-child': {
                paddingBottom: 16,
              },
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            },
            '& .lex-select-card__icon': {
              borderRadius: 8,
              border: `4px solid ${neutral[100]}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 48,
              width: 48,
              backgroundColor: neutral[25],
              '& .MuiSvgIcon-root': {
                fontSize: '24px',
                color: neutral[600],
              },
            },
          },
          '&.lex-form-card': {
            borderRadius: 4,
            border: `solid 1px ${neutral[200]}`,
            '& .MuiCardContent-root': {
              padding: 0,
            },
            '& .lex-form-card-header': {
              borderBottom: `solid 1px ${neutral[200]}`,
              backgroundColor: neutral[25],
              padding: '12px 16px',
              '& .MuiCardHeader-title': {
                fontSize: '16px',
                lineHeight: 1.5,
                fontWeight: 500,
                letterSpacing: '0.1px',
              },
            },
            '& .lex-form-card-content': {
              padding: '12px 16px',
            },
          },
          '&.lex-form': {
            borderRadius: 12,
            border: `solid 1px ${neutral[200]}`,
            '& .MuiCardActions-root': {
              borderTop: `solid 1px ${neutral[200]}`,
              padding: '16px 28px',
              display: 'flex',
              justifyContent: 'space-between',
            },
          },
          '&.solution-card': {
            borderRadius: 12,
            border: `solid 1px ${neutral[200]}`,
            backgroundColor: neutral[25],
            overflow: 'hidden',
            '& .MuiCardHeader-root': {
              borderBottom: `solid 1px ${neutral[100]}`,
              backgroundColor: neutral[25],
            },
            '& .MuiCardActions-root': {
              borderTop: `solid 1px ${neutral[100]}`,
              backgroundColor: neutral[25],
            },
          },
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          paddingBottom: 0,
          '& .MuiCardHeader-title': {
            fontSize: '1rem',
            fontWeight: 500,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontSize: '0.75rem',
          fontWeight: 600,
          height: '1.25rem',
          lineHeight: '1.25rem',
          '& .MuiChip-label': {
            paddingLeft: 4,
            paddingRight: 4,
          },
          '&.MuiChip-filledInfo': {
            backgroundColor: primary[100],
            color: primary[800],
          },
          '&.chip-select': {
            cursor: 'pointer',
            backgroundColor: neutral[25],
            color: neutral[700],
            border: `1px solid ${neutral[200]}`,
            padding: '0 8px',
            height: '24px',
            transition: 'all 0.2s ease-in-out',
            '& .MuiChip-label': {
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '24px',
            },
            '&:hover': {
              backgroundColor: primary[100],
              borderColor: primary[100],
            },
            '&:active': {
              backgroundColor: primary[100],
              borderColor: primary[100],
            },
            '&.selected': {
              backgroundColor: primary[100],
              color: primary[800],
              border: `1px solid ${primary[100]}`,
              '&:hover': {
                backgroundColor: primary[100],
                borderColor: primary[100],
              },
              '&:active': {
                backgroundColor: primary[100],
              },
            },
          },
          '&.filter-chip': {
            justifyContent: 'space-between',
            gap: 2,
            height: 32,
            minHeight: 32,
            backgroundColor: neutral[25],
            border: `1px solid ${neutral[100]}`,
            borderRadius: 4,
            '& .MuiChip-label': {
              lineHeight: '32px',
              padding: 2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: neutral[900],
              fontSize: '12px',
              fontWeight: 400,
              '& .filter-chip-label': {
                color: neutral[500],
              },
            },
            '& .MuiChip-deleteIcon': {
              color: 'text.secondary',
              '&:hover': {
                color: 'error.main',
              },
            },
          },
        },
      },
    },
    MuiContainer: {
      defaultProps: {
        disableGutters: true,
        sx: {
          p: 4,
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          backgroundColor: neutral[25],
          paddingTop: '2rem',
          // Style ReactSelect components within FormControl
          '& .react-select__control': {
            borderColor: neutral[200],
            '&:hover': {
              borderColor: primary[500],
            },
            '&.react-select__control--is-focused': {
              borderColor: primary[800],
              boxShadow: `0 0 0 1px ${primary[800]}`,
            },
          },
          '&.Mui-error .react-select__control': {
            borderColor: error[500],
            '&:hover': {
              borderColor: error[500],
            },
            '&.react-select__control--is-focused': {
              borderColor: error[500],
              boxShadow: `0 0 0 1px ${error[500]}`,
            },
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          margin: '4px 0 0',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: neutral[25],
        },
      },
    },
    MuiInputLabel: {
      defaultProps: {
        shrink: true,
      },
      styleOverrides: {
        root: {
          fontSize: '0.95rem',
        },
        shrink: {
          transform: 'translate(0px, 8px) scale(1) !important',
          backgroundColor: 'transparent',
        },
      },
    },
    MuiLink: {
      defaultProps: {
        component: LinkBehavior,
      },
      styleOverrides: {
        root: {
          textDecoration: 'none',
        },
      },
    },
    MuiListItemButton: {
      defaultProps: {
        LinkComponent: NavLinkBehavior,
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontWeight: 'bold',
          '& .MuiTypography-root': {
            fontWeight: 'bold',
          },
        },
      },
    },
    MuiOutlinedInput: {
      defaultProps: {
        notched: false,
      },
      styleOverrides: {
        root: {
          boxShadow: shadowsArr[1],
        },
        notchedOutline: {
          borderColor: neutral[200],
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        variant: 'outlined',
        size: 'medium',
        MenuProps: {
          PaperProps: {
            sx: {
              maxHeight: '50vh',
            },
          },
        },
      },
    },
    MuiStack: {
      styleOverrides: {
        root: {
          '&.lex-toolbar': {
            backgroundColor: 'primary.light',
            borderRadius: 3,
            padding: '6px 0',
            display: 'flex',
          },
          '&.lex-toolbar--mobile': {
            display: 'grid',
            '& *': {
              flexGrow: 1,
            },
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 52,
          height: 32,
          marginRight: 8,
          '& .MuiSwitch-track': {
            backgroundColor: neutral[300],
            borderRadius: 48,
            opacity: 1,
            height: '100%',
          },
          '& .MuiSwitch-switchBase': {
            padding: 0,
            top: 2,
            left: 2,
            '&.Mui-checked': {
              transform: 'translateX(20px)',
            },
          },
          '& .MuiSwitch-switchBase .MuiSwitch-thumb': {
            height: 28,
            width: 28,
            borderRadius: '50%',
            backgroundColor: neutral[25],
            boxShadow: '0px 3px 1px rgba(0, 0, 0, 0.06)',
          },
          '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': {
            backgroundColor: neutral[25],
            boxShadow: '0px 3px 1px rgba(0, 0, 0, 0.06)',
          },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            backgroundColor: success[350],
            opacity: 1,
          },
          '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
            opacity: 0.5,
          },
          '& .MuiSwitch-switchBase.Mui-checked.Mui-disabled + .MuiSwitch-track': {
            opacity: 0.5,
          },
          '&.Mui-disabled': {
            opacity: 0.5,
          },
          // Medium size
          '&.MuiSwitch-sizeMedium': {
            width: 52,
            height: 32,
            padding: 0,
            '& .MuiSwitch-switchBase': {
              top: 2,
              left: 2,
              '&.Mui-checked': {
                transform: 'translateX(20px)',
              },
            },
            '& .MuiSwitch-switchBase .MuiSwitch-thumb': {
              height: 28,
              width: 28,
            },
          },
          // Small size
          '&.MuiSwitch-sizeSmall': {
            width: 44,
            height: 24,
            padding: 0,
            '& .MuiSwitch-switchBase': {
              top: 2,
              left: 2,
              '&.Mui-checked': {
                transform: 'translateX(20px)',
              },
            },
            '& .MuiSwitch-switchBase .MuiSwitch-thumb': {
              height: 20,
              width: 20,
            },
          },
          // Extra small size
          '&.MuiSwitch-sizeXs': {
            width: 34,
            height: 20,
            padding: 0,
            '& .MuiSwitch-track': {
              height: '100%',
            },
            '& .MuiSwitch-switchBase': {
              padding: 0,
              top: 2,
              left: 2,
              '&.Mui-checked': {
                transform: 'translateX(14px)',
              },
            },
            '& .MuiSwitch-switchBase .MuiSwitch-thumb': {
              height: 16,
              width: 16,
              borderRadius: '50%',
              backgroundColor: neutral[25],
              boxShadow: '0px 2px 1px rgba(0, 0, 0, 0.06)',
            },
            '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': {
              backgroundColor: neutral[25],
              boxShadow: '0px 2px 1px rgba(0, 0, 0, 0.06)',
            },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: success[350],
              opacity: 1,
            },
            '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
              opacity: 0.5,
            },
            '& .MuiSwitch-switchBase.Mui-checked.Mui-disabled + .MuiSwitch-track': {
              opacity: 0.5,
            },
          },
        },
      },
    },
    MuiTable: {
      defaultProps: {
        stickyHeader: true,
      },
      styleOverrides: {
        root: {
          borderWidth: 1,
          borderColor: neutral[200],
          borderStyle: 'solid',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          '&.lex-tabs': {
            borderBottom: 1,
            borderColor: 'divider',
            marginBottom: 2,
            backgroundColor: 'transparent',
            width: '100%',
            '& .MuiTabs-scroller': {
              borderBottom: `1px solid ${neutral[100]}`,
            },
            '& .MuiTab-root': {
              height: '48px',
              padding: '12px 20px !important',
              borderRadius: 0,
              borderBottom: '1px solid transparent',
              color: neutral[500],
              textTransform: 'none',
              fontWeight: '400 !important',
              minHeight: '48px',
              opacity: 1,
              '& .MuiTypography-root-subtitleMd': {
                color: neutral[500],
              },
              '& .lex-badge-counter': {
                borderColor: neutral[200],
                color: neutral[500],
              },
              '&:hover': {
                borderBottom: `1px solid ${neutral[400]}`,
                color: neutral[700],
                '& .MuiTypography-root-subtitleMd': {
                  color: neutral[700],
                },
              },
              '&.Mui-selected': {
                color: primary[800],
                borderBottom: `2px solid ${primary[800]}`,
                '& .MuiTypography-root-subtitleMd': {
                  color: primary[800],
                },
                '& .lex-badge-counter': {
                  borderColor: primary[800],
                  color: neutral[25],
                },
              },
              '&.Mui-disabled': {
                backgroundColor: neutral[100],
                color: neutral[400],
                '& .MuiTypography-root-subtitleMd': {
                  color: neutral[400],
                },
                '& .lex-badge-counter': {
                  borderColor: neutral[200],
                  color: neutral[400],
                },
              },
            },
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'medium',
      },
    },
    MuiToolbar: {
      defaultProps: {
        disableGutters: true,
      },
      styleOverrides: {
        root: {
          '&.phx-toolbar-view': {
            height: 40,
            paddingLeft: 12,
          },
          '&.phx-toolbar-edit': {
            borderWidth: 1,
            backgroundColor: neutral[50],
            borderColor: neutral[100],
            borderStyle: 'solid',
            borderRadius: 12,
            padding: 16,
          },
          '&.phx-toolbar': {
            borderWidth: 1,
            backgroundColor: neutral[50],
            borderColor: neutral[100],
            borderStyle: 'solid',
            borderRadius: 12,
            padding: 24,
          },
          '&.phx-panel-footer, &.lex-panel-footer': {
            borderTop: `solid 1px ${neutral[200]}`,
            backgroundColor: neutral[25],
            padding: '0 16px',
          },
          '&.phx-panel-header, &.lex-panel-header': {
            borderBottom: `solid 1px ${neutral[200]}`,
            backgroundColor: neutral[25],
            padding: '0 16px',
          },
          '&.phx-tree-toolbar, &.lex-tree-toolbar': {
            padding: '0 12px 0 4px',
            minHeight: 0,
          },
          '&.phx-menubar, &.lex-menubar': {
            height: 44,
            minHeight: 44,
            borderBottom: `solid 1px ${neutral[200]}`,
            padding: '0 16px',
            backgroundColor: neutral[50],
          },
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        arrow: true,
      },
    },
    MuiTypography: {
      defaultProps: {
        component: 'div',
      },
      styleOverrides: {
        root: {
          fontFamily: 'Inter',
        },
      },
    },
  },
  palette: {
    primary: {
      main: '#004B87',
      dark: '#04294D',
      light: '#EFF7FF',
    },
    neutral: {
      main: neutral[400],
      dark: neutral[200],
      light: neutral[50],
      contrastText: neutral[800],
    },
    error: {
      main: error[500],
    },
  },
  shadows: shadowsArr,
  spacing: 4,
});

export default phoenixTheme;
